import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../config/axios';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { formatRut } from '../utils/validations';
import RegistroExitoso from './RegistroExitoso';

const Registro = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    password: '',
    confirmPassword: '',
    rut: '',
    aceptaTerminos: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [registroExitoso, setRegistroExitoso] = useState(false);

  const passwordRequirements = [
    { text: 'Al menos 8 caracteres', regex: /.{8,}/ },
    { text: 'Al menos una letra mayúscula', regex: /[A-Z]/ },
    { text: 'Al menos una letra minúscula', regex: /[a-z]/ },
    { text: 'Al menos un número', regex: /[0-9]/ },
    { text: 'Al menos un carácter especial', regex: /[!@#$%^&*]/ }
  ];

  const validatePassword = (password) => {
    const requirements = passwordRequirements.filter(req => !req.regex.test(password));
    return requirements.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar todos los campos obligatorios
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es obligatorio';
    if (!formData.apellidos.trim()) newErrors.apellidos = 'Los apellidos son obligatorios';
    if (!formData.email.trim()) newErrors.email = 'El email es obligatorio';
    if (!formData.rut.trim()) newErrors.rut = 'El RUT es obligatorio';
    if (!formData.password) newErrors.password = 'La contraseña es obligatoria';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Debe confirmar la contraseña';
    if (!formData.aceptaTerminos) newErrors.aceptaTerminos = 'Debe aceptar los términos y condiciones';

    // Validaciones específicas
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!validatePassword(formData.password)) {
      newErrors.password = 'La contraseña no cumple con los requisitos mínimos';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post('/auth/registro', {
        nombre: formData.nombre.trim(),
        apellidos: formData.apellidos.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        rut: formData.rut.trim()
      });

      if (response.data) {
        setRegistroExitoso(true);
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Registro exitoso. Por favor, inicia sesión.' 
            } 
          });
        }, 2000);
      }
    } catch (error) {
      console.error('Error en registro:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          submit: error.response?.data?.error || 'Error al registrar usuario'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    let newValue = value;

    if (name === 'nombre' || name === 'apellidos') {
      newValue = value.replace(/[^a-záéíóúüñÁÉÍÓÚÜÑ\s]/gi, '');
    }

    if (name === 'rut') {
      newValue = formatRut(value);
    }

    if (name === 'email') {
      newValue = value.toLowerCase();
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'aceptaTerminos' ? checked : newValue
    }));

    if (!touched[name]) {
      setTouched(prev => ({
        ...prev,
        [name]: true
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    // Validaciones específicas al perder el foco
    if (name === 'password' && formData.password && !validatePassword(formData.password)) {
      setErrors(prev => ({
        ...prev,
        password: 'La contraseña no cumple con los requisitos mínimos'
      }));
    }

    if (name === 'confirmPassword' && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setErrors(prev => ({
        ...prev,
        confirmPassword: 'Las contraseñas no coinciden'
      }));
    }
  };

  if (registroExitoso) {
    return <RegistroExitoso />;
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Registro de Paciente
          </Typography>

          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nombre && !!errors.nombre}
                  helperText={touched.nombre && errors.nombre}
                  required
                  inputProps={{
                    maxLength: 50
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  name="apellidos"
                  value={formData.apellidos}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.apellidos && !!errors.apellidos}
                  helperText={touched.apellidos && errors.apellidos}
                  required
                  inputProps={{
                    maxLength: 50
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="RUT"
                  name="rut"
                  value={formData.rut}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.rut && !!errors.rut}
                  helperText={touched.rut && errors.rut}
                  required
                  placeholder="12.345.678-9"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <FormHelperText>
                  {passwordRequirements.map((req, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      display="block"
                      color={formData.password && req.regex.test(formData.password) ? 'success.main' : 'text.secondary'}
                    >
                      • {req.text}
                    </Typography>
                  ))}
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirmar Contraseña"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="aceptaTerminos"
                      checked={formData.aceptaTerminos}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Acepto los{' '}
                      <Link component={RouterLink} to="/terminos-condiciones">
                        términos y condiciones
                      </Link>
                    </Typography>
                  }
                />
                {touched.aceptaTerminos && errors.aceptaTerminos && (
                  <FormHelperText error>
                    {errors.aceptaTerminos}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Registrarse'}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              ¿Ya tienes una cuenta?{' '}
              <Link component={RouterLink} to="/login">
                Inicia sesión
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Registro; 